import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Heading,
  useToast,
  Switch,
  Grid,
  Text,
  Divider,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import axios from "axios";

const SERVER = import.meta.env.VITE_API_URL;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{10}$/;
const GST_REGEX = /^[0-9A-Z]{15}$/;
const NUMERIC_ONLY_FIELDS = [
  "mobile",
  "whatsapp",
  "passengerMobile",
  "passengerWhatsapp",
];

const validateField = (name, value, formData) => {
  const normalized = typeof value === "string" ? value.trim() : value;

  switch (name) {
    case "name":
      if (!normalized) return "Full name is required";
      if (normalized.length < 2) return "Full name must be at least 2 characters";
      return "";

    case "email":
      if (!normalized) return "Email is required";
      if (!EMAIL_REGEX.test(normalized)) return "Enter a valid email address";
      return "";

    case "mobile":
      if (!normalized) return "Mobile number is required";
      if (!PHONE_REGEX.test(normalized.replace(/\s+/g, ""))) {
        return "Enter a valid mobile number (10 digits)";
      }
      return "";

    case "whatsapp":
      if (!normalized) return "WhatsApp number is required";
      if (!PHONE_REGEX.test(normalized.replace(/\s+/g, ""))) {
        return "Enter a valid WhatsApp number (10 digits)";
      }
      return "";

    case "city":
      if (!normalized) return "City is required";
      if (normalized.length < 2) return "City must be at least 2 characters";
      return "";

    case "gender":
      if (!normalized) return "Gender is required";
      return "";

    case "language":
      if (!normalized) return "Language is required";
      return "";

    case "status":
      if (!normalized) return "Status is required";
      return "";

    case "accountCategory":
      if (!normalized) return "Account category is required";
      return "";

    case "rating": {
      if (normalized === "" || normalized === null || normalized === undefined) {
        return "Rating is required";
      }
      const numeric = Number(normalized);
      if (Number.isNaN(numeric)) return "Rating must be a number";
      if (numeric < 0 || numeric > 5) return "Rating must be between 0 and 5";
      return "";
    }

    case "comments":
      if (!normalized) return "Comments are required";
      if (normalized.length < 3) return "Comments must be at least 3 characters";
      return "";

    case "notes":
      if (!normalized) return "Notes are required";
      if (normalized.length < 3) return "Notes must be at least 3 characters";
      return "";

    case "companyName":
      if (!formData.isCorporate) return "";
      if (!normalized) return "Company name is required";
      if (normalized.length < 2) {
        return "Company name must be at least 2 characters";
      }
      return "";

    case "gstNo":
      if (!formData.isCorporate) return "";
      if (!normalized) return "GST number is required";
      if (!GST_REGEX.test(normalized.toUpperCase())) {
        return "GST number must be 15 alphanumeric characters";
      }
      return "";

    case "passengerName":
      if (formData.passengerSameAsCustomer) return "";
      if (!normalized) return "Passenger name is required";
      if (normalized.length < 2) {
        return "Passenger name must be at least 2 characters";
      }
      return "";

    case "passengerEmail":
      if (formData.passengerSameAsCustomer) return "";
      if (!normalized) return "Passenger email is required";
      if (!EMAIL_REGEX.test(normalized)) {
        return "Enter a valid passenger email address";
      }
      return "";

    case "passengerMobile":
      if (formData.passengerSameAsCustomer) return "";
      if (!normalized) return "Passenger mobile is required";
      if (!PHONE_REGEX.test(normalized.replace(/\s+/g, ""))) {
        return "Enter a valid passenger mobile number (10 digits)";
      }
      return "";

    case "passengerWhatsapp":
      if (formData.passengerSameAsCustomer) return "";
      if (!normalized) return "Passenger WhatsApp is required";
      if (!PHONE_REGEX.test(normalized.replace(/\s+/g, ""))) {
        return "Enter a valid passenger WhatsApp number (10 digits)";
      }
      return "";

    default:
      return "";
  }
};

const validateForm = (formData) => {
  const fields = [
    "name",
    "email",
    "mobile",
    "whatsapp",
    "city",
    "gender",
    "language",
    "status",
    "accountCategory",
    "rating",
    "comments",
    "notes",
    "companyName",
    "gstNo",
    "passengerName",
    "passengerEmail",
    "passengerMobile",
    "passengerWhatsapp",
  ];

  return fields.reduce((acc, field) => {
    const error = validateField(field, formData[field], formData);
    if (error) {
      acc[field] = error;
    }
    return acc;
  }, {});
};

const AddCustomer = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    whatsapp: "",
    city: "",
    gender: "Male",
    language: "English, Hindi",
    comments: "",
    isCorporate: false,
    type: "Individual",
    rating: 0,
    notes: "",
    status: "Active",
    accountCategory: "Classic",
    companyName: "",
    gstNo: "",
    passengerSameAsCustomer: true,
    passengerName: "",
    passengerEmail: "",
    passengerMobile: "",
    passengerWhatsapp: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const sanitizedValue = NUMERIC_ONLY_FIELDS.includes(name)
      ? value.replace(/\D/g, "").slice(0, 10)
      : value;
    const nextFormData =
      type === "checkbox"
        ? {
          ...formData,
          [name]: checked,
          ...(name === "isCorporate" && !checked && { type: "Individual" }),
          ...(name === "isCorporate" && checked && { type: "Corporate" }),
        }
        : {
          ...formData,
          [name]: sanitizedValue,
        };

    setFormData(nextFormData);

    if (touched[name] || submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, nextFormData[name], nextFormData),
      }));
    }

    if (name === "isCorporate") {
      setErrors((prev) => ({
        ...prev,
        companyName: validateField("companyName", nextFormData.companyName, nextFormData),
        gstNo: validateField("gstNo", nextFormData.gstNo, nextFormData),
      }));
    }

    if (name === "passengerSameAsCustomer") {
      setErrors((prev) => ({
        ...prev,
        passengerName: validateField(
          "passengerName",
          nextFormData.passengerName,
          nextFormData,
        ),
        passengerEmail: validateField(
          "passengerEmail",
          nextFormData.passengerEmail,
          nextFormData,
        ),
        passengerMobile: validateField(
          "passengerMobile",
          nextFormData.passengerMobile,
          nextFormData,
        ),
        passengerWhatsapp: validateField(
          "passengerWhatsapp",
          nextFormData.passengerWhatsapp,
          nextFormData,
        ),
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, formData[name], formData),
    }));
  };

  const hasError = (field) => Boolean((touched[field] || submitAttempted) && errors[field]);

  const markAllTouched = () => {
    setTouched(
      Object.keys(formData).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {}),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    markAllTouched();

    if (Object.keys(validationErrors).length > 0) {
      toast({
        title: "Validation Error",
        description: "Please correct the highlighted fields before submitting",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("prima_session");

      if (!token) {
        throw new Error("Token not found. Please login again.");
      }

      // Prepare data to send
      const dataToSend = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile: formData.mobile.trim(),
        whatsapp: formData.whatsapp.trim(),
        city: formData.city.trim(),
        gender: formData.gender,
        language: formData.language.trim(),
        comments: formData.comments.trim(),
        isCorporate: formData.isCorporate,
        type: formData.type,
        rating: parseFloat(formData.rating) || 0,
        notes: formData.notes.trim(),
        status: formData.status,
        accountCategory: formData.accountCategory,
      };

      // Add corporate details if applicable
      if (formData.isCorporate) {
        dataToSend.companyName = formData.companyName.trim();
        dataToSend.gstNo = formData.gstNo.trim().toUpperCase();
      }

      // Add passenger details if not same as customer
      if (!formData.passengerSameAsCustomer) {
        dataToSend.passengerSameAsCustomer = false;
        dataToSend.passengerName = formData.passengerName.trim();
        dataToSend.passengerEmail = formData.passengerEmail.trim();
        dataToSend.passengerMobile = formData.passengerMobile.trim();
        dataToSend.passengerWhatsapp = formData.passengerWhatsapp.trim();
      } else {
        dataToSend.passengerSameAsCustomer = true;
      }

      const response = await axios.post(
        `${SERVER}/management/customer`,
        dataToSend,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      toast({
        title: "Success",
        description: "Customer added successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Navigate back to customers list
      navigate("/management/customer");
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: error.response?.data?.message || error.message || "Failed to add customer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box px={4} py={6}>
      <Flex
        justifyContent={{ base: "center", md: "space-between" }}
        alignItems={{ base: "flex-start", md: "center" }}
        mb={6}
        flexDir={{ base: "column", md: "row" }}
      >
        <BreadCrumbMaker
          items={[
            { link: "/management", title: "Operations Setup" },
            { link: "/management/customer", title: "Customers" },
            { link: "/management/customers/create", title: "Add Customer", on: true },
          ]}
        />
      </Flex>

      <Box bg="white" p={8} borderRadius="lg" boxShadow="sm">
        <Heading size="md" mb={6} color="#1A202C">
          Add New Customer
        </Heading>

        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Basic Information */}
            <Box>
              <Heading size="sm" mb={4} color="#1A202C">
                Basic Information
              </Heading>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                <FormControl isRequired isInvalid={hasError("name")}>
                  <FormLabel>Full Name</FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter full name"
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasError("email")}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter email"
                  />
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasError("mobile")}>
                  <FormLabel>Mobile Number</FormLabel>
                  <Input
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={10}
                    placeholder="Enter 10 digit number"
                  />
                  <FormErrorMessage>{errors.mobile}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasError("whatsapp")}>
                  <FormLabel>WhatsApp Number</FormLabel>
                  <Input
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={10}
                    placeholder="Enter 10 digit number"
                  />
                  <FormErrorMessage>{errors.whatsapp}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasError("city")}>
                  <FormLabel>City</FormLabel>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter city"
                  />
                  <FormErrorMessage>{errors.city}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasError("gender")}>
                  <FormLabel>Gender</FormLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Select>
                  <FormErrorMessage>{errors.gender}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasError("language")}>
                  <FormLabel>Language</FormLabel>
                  <Input
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="e.g., English, Hindi"
                  />
                  <FormErrorMessage>{errors.language}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasError("status")}>
                  <FormLabel>Status</FormLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Select>
                  <FormErrorMessage>{errors.status}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasError("accountCategory")}>
                  <FormLabel>Account Category</FormLabel>
                  <Select
                    name="accountCategory"
                    value={formData.accountCategory}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="Classic">Classic</option>
                    <option value="Premium">Premium</option>
                    <option value="VIP">VIP</option>
                  </Select>
                  <FormErrorMessage>{errors.accountCategory}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasError("rating")}>
                  <FormLabel>Rating</FormLabel>
                  <Input
                    name="rating"
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={formData.rating}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="0.0"
                  />
                  <FormErrorMessage>{errors.rating}</FormErrorMessage>
                </FormControl>
              </Grid>
            </Box>

            <Divider />

            {/* Corporate Information */}
            <Box>
              <Flex alignItems="center" mb={4} gap={4}>
                <Heading size="sm" color="#1A202C">
                  Corporate Information
                </Heading>
                <FormControl display="flex" alignItems="center" width="auto">
                  <FormLabel mb="0" mr={2}>
                    Is Corporate?
                  </FormLabel>
                  <Switch
                    name="isCorporate"
                    isChecked={formData.isCorporate}
                    onChange={handleChange}
                    colorScheme="blue"
                  />
                </FormControl>
              </Flex>

              {formData.isCorporate && (
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                  <FormControl
                    isRequired={formData.isCorporate}
                    isInvalid={hasError("companyName")}
                  >
                    <FormLabel>Company Name</FormLabel>
                    <Input
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter company name"
                    />
                    <FormErrorMessage>{errors.companyName}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired={formData.isCorporate}
                    isInvalid={hasError("gstNo")}
                  >
                    <FormLabel>GST Number</FormLabel>
                    <Input
                      name="gstNo"
                      value={formData.gstNo}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter GST number"
                    />
                    <FormErrorMessage>{errors.gstNo}</FormErrorMessage>
                  </FormControl>
                </Grid>
              )}
            </Box>

            <Divider />

            {/* Passenger Information */}
            <Box>
              <Flex alignItems="center" mb={4} gap={4}>
                <Heading size="sm" color="#1A202C">
                  Passenger Information
                </Heading>
                <FormControl display="flex" alignItems="center" width="auto">
                  <FormLabel mb="0" mr={2}>
                    Same as Customer?
                  </FormLabel>
                  <Switch
                    name="passengerSameAsCustomer"
                    isChecked={formData.passengerSameAsCustomer}
                    onChange={handleChange}
                    colorScheme="blue"
                  />
                </FormControl>
              </Flex>

              {!formData.passengerSameAsCustomer && (
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                  <FormControl
                    isRequired={!formData.passengerSameAsCustomer}
                    isInvalid={hasError("passengerName")}
                  >
                    <FormLabel>Passenger Name</FormLabel>
                    <Input
                      name="passengerName"
                      value={formData.passengerName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter passenger name"
                    />
                    <FormErrorMessage>{errors.passengerName}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired={!formData.passengerSameAsCustomer}
                    isInvalid={hasError("passengerEmail")}
                  >
                    <FormLabel>Passenger Email</FormLabel>
                    <Input
                      name="passengerEmail"
                      type="email"
                      value={formData.passengerEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter passenger email"
                    />
                    <FormErrorMessage>{errors.passengerEmail}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired={!formData.passengerSameAsCustomer}
                    isInvalid={hasError("passengerMobile")}
                  >
                    <FormLabel>Passenger Mobile</FormLabel>
                    <Input
                      name="passengerMobile"
                      value={formData.passengerMobile}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={10}
                      placeholder="Enter 10 digit number"
                    />
                    <FormErrorMessage>{errors.passengerMobile}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isRequired={!formData.passengerSameAsCustomer}
                    isInvalid={hasError("passengerWhatsapp")}
                  >
                    <FormLabel>Passenger WhatsApp</FormLabel>
                    <Input
                      name="passengerWhatsapp"
                      value={formData.passengerWhatsapp}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={10}
                      placeholder="Enter 10 digit number"
                    />
                    <FormErrorMessage>{errors.passengerWhatsapp}</FormErrorMessage>
                  </FormControl>
                </Grid>
              )}
            </Box>

            <Divider />

            {/* Additional Information */}
            <Box>
              <Heading size="sm" mb={4} color="#1A202C">
                Additional Information
              </Heading>
              <Grid templateColumns="1fr" gap={4}>
                <FormControl isRequired isInvalid={hasError("comments")}>
                  <FormLabel>Comments</FormLabel>
                  <Textarea
                    name="comments"
                    value={formData.comments}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter any comments or preferences"
                    rows={3}
                  />
                  <FormErrorMessage>{errors.comments}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={hasError("notes")}>
                  <FormLabel>Notes</FormLabel>
                  <Textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter any internal notes"
                    rows={3}
                  />
                  <FormErrorMessage>{errors.notes}</FormErrorMessage>
                </FormControl>
              </Grid>
            </Box>

            {/* Action Buttons */}
            <Flex gap={4} justifyContent="flex-end" mt={6}>
              <Button
                variant="outline"
                onClick={() => navigate("/management/customer")}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={loading}
                loadingText="Adding Customer..."
              >
                Add Customer
              </Button>
            </Flex>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default AddCustomer;